/**
 * 签到管理 API
 * 后端：/api/sign-in-management
 * 管理端可查看所有班级，教师端只能查看自己的班级（后端根据角色过滤）
 */
import { request, requestRaw, buildUrl } from './request'

function requestSignIn(path, options = {}) {
  return request(path, options)
}

function parseDownloadFileName(contentDisposition, fallback) {
  if (!contentDisposition) return fallback

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1])
    } catch {
      return utf8Match[1]
    }
  }

  const simpleMatch = contentDisposition.match(/filename="?([^"]+)"?/i)
  return simpleMatch?.[1] || fallback
}

async function extractSignInDownloadError(response, fallbackMessage) {
  const payload = await response.json().catch(() => null)
  if (payload?.message) return payload.message
  if (payload?.error) return payload.error
  if (payload?.msg) return payload.msg
  return fallbackMessage
}

function normalizeAttendanceConfigScope(scopeType, scopeId) {
  if (scopeType === undefined || scopeType === null || scopeType === '') {
    return {
      scope_type: scopeType,
      scope_id: scopeId
    }
  }
  const normalizedScopeType = Number(scopeType)
  if (Number.isFinite(normalizedScopeType) && normalizedScopeType === 1) {
    return {
      scope_type: 1,
      scope_id: 0
    }
  }
  return {
    scope_type: scopeType,
    scope_id: scopeId
  }
}

function normalizeAttendanceConfigData(data = {}) {
  const payload = { ...data }
  const normalized = normalizeAttendanceConfigScope(payload.scope_type, payload.scope_id)
  if (normalized.scope_type !== undefined) payload.scope_type = normalized.scope_type
  if (normalized.scope_id !== undefined) payload.scope_id = normalized.scope_id
  return payload
}

/** 签到类型：1-早自习，2-下午签到，3-晚自习 */
export const SIGN_TYPE = {
  MORNING_STUDY: 1,
  AFTERNOON_SIGN: 2,
  EVENING_STUDY: 3
}

export const SIGN_TYPE_LABEL = {
  1: '早自习',
  2: '下午签到',
  3: '晚自习'
}

/**
 * 查看所有学生签到记录（分页）
 * GET /api/sign-in-management/all-students
 * @param {object} params - { class_id?, class_ids?, grade_id?, grade?, sign_type?, period_config_id?, time_scope?, student_name?, start_date?, end_date?, page?, page_size? }
 */
export function getAllStudentsSignIn(params = {}) {
  const isOtherScope = params.time_scope === 'other' || params.period_config_id === 'other'
  const classIds = Array.isArray(params.class_ids)
    ? params.class_ids.map(item => String(item ?? '').trim()).filter(Boolean).join(',')
    : String(params.class_ids ?? '').trim()
  const url = buildUrl('/api/sign-in-management/all-students', {
    class_id: params.class_id,
    class_ids: classIds,
    grade_id: params.grade_id,
    grade: params.grade,
    period_order: params.period_order ?? params.sign_type,
    period_config_id: isOtherScope ? undefined : params.period_config_id,
    time_scope: isOtherScope ? 'other' : params.time_scope,
    student_name: params.student_name,
    start_date: params.start_date,
    end_date: params.end_date,
    page: params.page ?? 1,
    page_size: params.page_size ?? 20
  })
  return requestSignIn(url)
}

/**
 * 查看班级学生签到详情
 * GET /api/sign-in-management/class/{class_id}/students
 * @param {number|string} classId - 班级 ID
 * @param {object} params - { sign_date?, period_order?, period_config_id? } sign_date 默认今天
 */
export function getClassStudentsSignIn(classId, params = {}) {
  const url = buildUrl(`/api/sign-in-management/class/${classId}/students`, {
    sign_date: params.sign_date,
    period_order: params.period_order,
    period_config_id: params.period_config_id
  })
  return requestSignIn(url)
}

/**
 * 导出班级考勤 Excel
 * GET /api/sign-in-management/class/{class_id}/export
 * @param {number|string} classId
 * @param {object} params - { start_date, end_date, period_order?, period_config_id? }
 * @param {string} fallbackFileName
 * @returns {Promise<{blob: Blob, fileName: string}>}
 */
export async function exportClassAttendance(classId, params = {}, fallbackFileName = '考勤导出.xlsx') {
  const url = buildUrl(`/api/sign-in-management/class/${classId}/export`, {
    start_date: params.start_date,
    end_date: params.end_date,
    period_order: params.period_order,
    period_config_id: params.period_config_id
  })

  const response = await requestRaw(url)

  if (!response.ok) {
    const message = await extractSignInDownloadError(response, '导出考勤失败')
    const error = new Error(message)
    error.status = response.status
    throw error
  }

  const blob = await response.blob()
  const fileName = parseDownloadFileName(response.headers.get('content-disposition'), fallbackFileName)

  return { blob, fileName }
}

export async function exportClassesAttendance(params = {}, fallbackFileName = '考勤导出.xlsx') {
  const classIds = Array.isArray(params.class_ids)
    ? params.class_ids.map(item => String(item ?? '').trim()).filter(Boolean).join(',')
    : String(params.class_ids ?? '').trim()
  const isOtherScope = params.time_scope === 'other' || params.period_config_id === 'other'

  const url = buildUrl('/api/sign-in-management/classes/export', {
    start_date: params.start_date,
    end_date: params.end_date,
    class_ids: classIds,
    period_order: params.period_order,
    period_config_id: isOtherScope ? undefined : params.period_config_id,
    time_scope: isOtherScope ? 'other' : params.time_scope,
    student_name: params.student_name
  })

  const response = await requestRaw(url)

  if (!response.ok) {
    const message = await extractSignInDownloadError(response, '导出考勤失败')
    const error = new Error(message)
    error.status = response.status
    throw error
  }

  const blob = await response.blob()
  const fileName = parseDownloadFileName(response.headers.get('content-disposition'), fallbackFileName)

  return { blob, fileName }
}

/**
 * 获取签到配置
 * GET /api/sign-in-management/configs
 * @returns {Promise<{ success, data: object }>}
 */
export function getSignInConfigs() {
  return requestSignIn('/api/sign-in-management/configs', { silent: true })
}

/**
 * 查看学生签到历史
 * GET /api/sign-in-management/student/{user_id}/history
 * @param {number|string} userId - 学生用户 ID
 * @param {object} params - { start_date?, end_date? } 默认近30天
 */
export function getStudentSignInHistory(userId, params = {}) {
  const url = buildUrl(`/api/sign-in-management/student/${userId}/history`, {
    start_date: params.start_date,
    end_date: params.end_date
  })
  return requestSignIn(url)
}

/**
 * 时间范围内签到统计
 * GET /api/sign-in-management/time-range-statistics
 * @param {object} params - { start_date, end_date, grade_id?, class_id?, sign_type?, period_config_id? }
 */
export function getTimeRangeStatistics(params) {
  const url = buildUrl('/api/sign-in-management/time-range-statistics', {
    start_date: params.start_date,
    end_date: params.end_date,
    grade_id: params.grade_id,
    grade: params.grade,
    class_id: params.class_id,
    period_order: params.period_order ?? params.sign_type,
    period_config_id: params.period_config_id
  })
  return requestSignIn(url)
}

/**
 * 获取今日签到情况
 * GET /api/sign-in-management/today-status
 * @param {object} params - { class_id?, period_config_id? } 不传则查询全部班级（管理端）
 */
export function getTodaySignInStatus(params = {}) {
  const url = buildUrl('/api/sign-in-management/today-status', {
    class_id: params.class_id,
    period_config_id: params.period_config_id
  })
  return requestSignIn(url)
}

// ==================== 考勤配置管理（管理端） ====================

/**
 * 获取考勤配置列表
 * GET /api/sign-in-management/attendance-configs
 */
export function getAttendanceConfigs(params = {}) {
  const normalized = normalizeAttendanceConfigScope(params.scope_type, params.scope_id)
  const url = buildUrl('/api/sign-in-management/attendance-configs', {
    scope_type: normalized.scope_type,
    scope_id: normalized.scope_id,
    include_periods: params.include_periods
  })
  return requestSignIn(url, { silent: true })
}

/**
 * 创建考勤配置
 * POST /api/sign-in-management/attendance-configs
 * @param {object} data - { config_name, scope_type, scope_id?, timezone, effective_start_date?, effective_end_date?, is_active? }
 */
export function createAttendanceConfig(data, params = {}) {
  const payload = normalizeAttendanceConfigData(data)
  const normalized = normalizeAttendanceConfigScope(
    params.scope_type ?? payload.scope_type,
    params.scope_id ?? payload.scope_id
  )
  const url = buildUrl('/api/sign-in-management/attendance-configs', {
    scope_type: normalized.scope_type,
    scope_id: normalized.scope_id,
    include_periods: params.include_periods
  })
  return requestSignIn(url, {
    method: 'POST',
    body: JSON.stringify(payload)
  })
}

/**
 * 获取单个考勤配置
 * GET /api/sign-in-management/attendance-configs/{config_id}
 */
export function getAttendanceConfig(configId) {
  return requestSignIn(`/api/sign-in-management/attendance-configs/${configId}`, { silent: true })
}

/**
 * 更新考勤配置
 * PUT /api/sign-in-management/attendance-configs/{config_id}
 */
export function updateAttendanceConfig(configId, data) {
  const payload = normalizeAttendanceConfigData(data)
  return requestSignIn(`/api/sign-in-management/attendance-configs/${configId}`, {
    method: 'PUT',
    body: JSON.stringify(payload)
  })
}

/**
 * 删除考勤配置
 * DELETE /api/sign-in-management/attendance-configs/{config_id}
 */
export function deleteAttendanceConfig(configId) {
  return requestSignIn(`/api/sign-in-management/attendance-configs/${configId}`, {
    method: 'DELETE'
  })
}

// ==================== 考勤时段管理（管理端） ====================

/**
 * 获取某配置下的时段列表
 * GET /api/sign-in-management/attendance-configs/{config_id}/periods
 */
export function getAttendanceConfigPeriods(configId, params = {}) {
  const url = buildUrl(`/api/sign-in-management/attendance-configs/${configId}/periods`, params)
  return requestSignIn(url, { silent: true })
}

/**
 * 在某配置下新增时段
 * POST /api/sign-in-management/attendance-configs/{config_id}/periods
 */
export function createAttendanceConfigPeriod(configId, data, params = {}) {
  const url = buildUrl(`/api/sign-in-management/attendance-configs/${configId}/periods`, {
    active_only: params.active_only
  })
  return requestSignIn(url, {
    method: 'POST',
    body: JSON.stringify(data)
  })
}

/**
 * 获取单个时段
 * GET /api/sign-in-management/attendance-periods/{period_config_id}
 */
export function getAttendancePeriod(periodConfigId) {
  return requestSignIn(`/api/sign-in-management/attendance-periods/${periodConfigId}`, { silent: true })
}

/**
 * 更新单个时段
 * PUT /api/sign-in-management/attendance-periods/{period_config_id}
 */
export function updateAttendancePeriod(periodConfigId, data) {
  return requestSignIn(`/api/sign-in-management/attendance-periods/${periodConfigId}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  })
}

/**
 * 删除单个时段
 * DELETE /api/sign-in-management/attendance-periods/{period_config_id}
 */
export function deleteAttendancePeriod(periodConfigId) {
  return requestSignIn(`/api/sign-in-management/attendance-periods/${periodConfigId}`, {
    method: 'DELETE'
  })
}
